import { useCallback, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Creators as DataCreators } from "model/data"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { createUuid, type Uuid } from "helpers/types/uuid"
import { warn } from "helpers/dev-logger"
import { useAppDispatch, useAppStore } from "hooks/redux"
import type { ReduxState } from "model/state"
import {
  useCreateInstance,
  useReseedInstance,
  useSaveInstanceWorkspace,
  DASHBOARD_INSTANCES_RESOURCE,
} from "resources/dashboard-instances"
import { useCreateLayout } from "resources/layouts"
import { instancesSlice } from "../store/instances-slice"
import { exportLayoutConfig } from "../layouts/export-layout-config"
import { serializeWorkspace, type WorkspaceSerializableState } from "../layouts/serialize-workspace"
import type { GetArrangement } from "./use-workspace-sync"

export interface DuplicateDashboard {
  // Never throws: toasts on success/failure, so the caller only awaits resolution.
  duplicate: (sourceLayoutId: Uuid, sourceName: string) => Promise<void>
  // True while a duplicate is in flight — a slow network + reopen-and-click would otherwise mint a
  // second copy, since the menu action itself has no dialog to disable against. [APP-3309]
  isDuplicating: boolean
}

/**
 * "Duplicate dashboard" (APP-2709).
 *
 * Clones the live dashboard the user is viewing into a NEW instance and routes to it. The copy links
 * to the SAME layout as the source — this is an instance-level duplicate, so no new layout row is
 * written. Its workspace is a snapshot of the source's live state, so instance-only bits (ai-artifact
 * panels, column widths) carry over — unlike "save as new layout", which deliberately drops them.
 *
 * @param dashboardId - The dashboard slot whose live slice state is the duplication source. In the
 *   routed model this equals the active instanceId.
 * @param getArrangement - Reads the current Dockview arrangement blob at call time, so the copy is
 *   WYSIWYG. Returns null (panels-only) when no engine is mounted.
 */
export function useDuplicateDashboard(
  dashboardId: string,
  getArrangement: GetArrangement,
): DuplicateDashboard {
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const createInstance = useCreateInstance()
  const createLayout = useCreateLayout()
  const reseedInstance = useReseedInstance()
  const saveWorkspace = useSaveInstanceWorkspace()
  const [isDuplicating, setIsDuplicating] = useState(false)

  const duplicate = useCallback(
    async (sourceLayoutId: Uuid, sourceName: string) => {
      if (isDuplicating) return
      setIsDuplicating(true)
      const copyName = `${sourceName} (copy)`
      try {
        // Snapshot live state at call time (not a render closure) so the copy is WYSIWYG.
        const arrangement = getArrangement()
        const state = store.getState() as unknown as WorkspaceSerializableState
        const workspace = serializeWorkspace(state, dashboardId, arrangement)

        // The copy links to the source's layout. If the source has none (legacy/never-seeded
        // instance), duplicating it as-is would mint another layout-less instance — and that copy
        // couldn't be duplicated either. Self-heal instead: promote the source's live state to a
        // layout and re-point the source at it, so both source and copy reference a real layout.
        let layoutId = sourceLayoutId
        if (!layoutId) {
          const { config } = exportLayoutConfig(state, dashboardId, arrangement)
          const layout = await createLayout(sourceName, config)
          const reseeded = await reseedInstance(dashboardId, layout.id)
          // Sync the registry slice and invalidate the list cache right away — not deferred to the
          // end of this function — so a retry after a later failure (or a second click before this
          // one resolves) sees the reseeded layoutId instead of re-entering self-heal and minting
          // another layout. Mirrors useSaveAsNewLayout. [APP-3266]
          dispatch(instancesSlice.actions.upsertInstance({
            instanceId: reseeded.instanceId,
            name: reseeded.name,
            layoutId: reseeded.layoutId,
            layoutVersionSeeded: reseeded.layoutVersionSeeded,
            lastOpenedAt: reseeded.lastOpenedAt,
          }))
          dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))
          layoutId = reseeded.layoutId
        }

        const instanceId = createUuid()
        const created = await createInstance({ instanceId, name: copyName, layoutId })

        // Best-effort: the instance already exists and is routable, and the open-time autosave
        // re-persists the workspace — a failed PUT must not strand the copy or trigger a retry.
        try {
          await saveWorkspace(instanceId, workspace)
        } catch (error) {
          warn("Duplicate workspace save failed; the open-time autosave will re-persist it", error)
        }

        // The cached list predates the copy; reset it so the route resolver refetches before we route.
        dispatch(DataCreators.resetCollections([DASHBOARD_INSTANCES_RESOURCE]))

        // Read account fresh from the store (the callback may resolve after an account switch).
        const accountAlias = (store.getState() as unknown as ReduxState).global.selected.account
        if (accountAlias) navigate(`/${accountAlias}/dashboard/${created.slug}`)

        dispatch(NotificationCreators.add("success", null, `Duplicated as "${copyName}".`))
      } catch {
        dispatch(NotificationCreators.add("error", null, "Couldn't duplicate the dashboard. Please try again."))
      } finally {
        setIsDuplicating(false)
      }
    },
    [
      store, dispatch, navigate, createInstance, createLayout, reseedInstance, saveWorkspace, dashboardId,
      getArrangement, isDuplicating,
    ],
  )

  return { duplicate, isDuplicating }
}
